import React, { useState } from "react";
import type {
    SolverBlockLogicOptions,
    SolverConfigs,
    SolverConstructionHeuristicOptions,
    SolverMandatoryEntityOptions,
    SolverPhase
} from "@/types.ts";
import type {
    SolverSettingOptionsController,
    SolverSettingsOptionsProps
} from "@/components/sections/SolverSettingsOptions.types.tsx";

function safeParseInputTimeSecondsToMilli(seconds: string): number {
    const parseSeconds = parseFloat(seconds || "0");
    if (isNaN(parseSeconds)) return 0;
    return Math.floor(parseSeconds * 1000);
}

function safeParseInputIntNumber(seconds: string): number {
    const parseSeconds = parseFloat(seconds || "0");
    if (isNaN(parseSeconds)) return 0;
    return Math.floor(parseSeconds);
}

function initOptionNumber(value: number | undefined | null, defaultInitValue: number): number {
    if (value === void 0 || value === null) return defaultInitValue;
    return value
}

const labels = {
    INPUT_MAX_TIME: 'Temps maximale (secondes)',
    //INPUT_ACC_COUNT_LIMIT: 'Limite de permutations pour acceptation',
    INPUT_ACC_COUNT_LIMIT: 'Quantité d\'essaies par scénario (0-2500)',
    INPUT_UNAPROVED_STEPS: 'Arrêt après X nombre de permutations non-approuvées',
    INPUT_TABU_SIZE: 'Empêcher une permutation déjà non-approuvée pour les X prochaines permutations'
}
const InputGroupWrapper: React.FC<{ children: React.ReactNode, onClick?: () => void }> = (props) => {
    return <div
        onClick={props.onClick}
        className={`p-4 border-1 border-gray-300 rounded-sm my-2` + (props.onClick ? ' cursor-pointer' : '')}>
        {props.children}
    </div>
}
const CheckBox: React.FC<{
    checked: boolean,
    onChange: React.ChangeEventHandler<HTMLInputElement, HTMLInputElement> | undefined
}> = (props) => {
    return <input
        type="checkbox"
        checked={props.checked}
        disabled={false}
        onChange={props.onChange}
        className="h-4 w-4 accent-blue-600"
    />
}


const ToggleField: React.FC<{
    title: string,
    checked: boolean,
    onChange: React.ChangeEventHandler<HTMLInputElement, HTMLInputElement> | undefined
    //children: React.ReactNode
}> = (props) => {
    return <div className="flex items-center gap-2">
        <CheckBox
            checked={props.checked}
            onChange={props.onChange}
        />
        <span className={`text-sm`}>{props.title}</span>
    </div>
}


const QtyInputField: React.FC<{
    title: string,
    value: string | number,
    max?: string,
    checkable?: boolean
    checked?: boolean,
    onCheckChange?: React.ChangeEventHandler<HTMLInputElement, HTMLInputElement> | undefined
    onChange: React.ChangeEventHandler<HTMLInputElement, HTMLInputElement>
    //children: React.ReactNode
}> = (props) => {
    return <div className="flex items-center gap-2 justify-start my-1">
        {
            props.checkable === true ? <CheckBox
                checked={props.checked === true}
                onChange={props.onCheckChange}
            /> : ''
        }
        <span className="text-xs font-semibold">{props.title}</span>
        <input
            type="number"
            min="0"
            max={props.value || '999'}
            disabled={false}
            value={props.value}
            onChange={props.onChange}
            className="w-16 py-1 text-center border border-gray-400 rounded focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
    </div>
}


export function buildSolverSettingController(solverConfigs: SolverPhase | undefined): SolverSettingOptionsController {
    const [editData, setEditData] = useState({ ...{}, ...solverConfigs });

    const DEFAULT_unimprovedStepCountLimit = 20;
    const DEFAULT_unimprovedStepCountLimit_bloc = 10;
    const DEFAULT_tabuSize = 1;

    const viewData = {
        useBlockLogic: editData.blockLogicOptions?.apply === true,
        blockLogicMaxTime: initOptionNumber(editData.blockLogicOptions?.maxTimeInMilli, (100 * 1000)) / 1000,
        blockLogicAcceptedCountLimit: initOptionNumber((editData.blockLogicOptions?.acceptedCountLimit), 1500),
        blockLogicUnimprovedStepCountLimit: initOptionNumber((editData.blockLogicOptions?.unimprovedStepCountLimit), DEFAULT_unimprovedStepCountLimit_bloc),
        blockLogicTabuSize: initOptionNumber((editData.blockLogicOptions?.tabuSize), DEFAULT_tabuSize),

        useMandatoryEntity: editData.mandatoryEntityOptions?.apply === true,
        mandatoryEntityMaxTime: initOptionNumber(editData.mandatoryEntityOptions?.maxTimeInMilli, (100 * 1000)) / 1000,
        mandatoryEntityAcceptedCountLimit: initOptionNumber((editData.mandatoryEntityOptions?.acceptedCountLimit), 500),
        mandatoryEntityUnimprovedStepCountLimit: initOptionNumber((editData.mandatoryEntityOptions?.unimprovedStepCountLimit), DEFAULT_unimprovedStepCountLimit),
        mandatoryEntityTabuSize: initOptionNumber((editData.mandatoryEntityOptions?.tabuSize), DEFAULT_tabuSize),

        useConstructionHeuristic: editData.constructionHeuristicOptions?.apply !== false,
        constructionHeuristicMaxTime: initOptionNumber(editData.constructionHeuristicOptions?.maxTimeInMilli, (100 * 1000)) / 1000,
        constructionHeuristicAcceptedCountLimit: initOptionNumber((editData.constructionHeuristicOptions?.acceptedCountLimit), 500),
        constructionHeuristicUnimprovedStepCountLimit: initOptionNumber((editData.constructionHeuristicOptions?.unimprovedStepCountLimit), DEFAULT_unimprovedStepCountLimit),
        constructionHeuristicTabuSize: initOptionNumber((editData.constructionHeuristicOptions?.tabuSize), DEFAULT_tabuSize),

        useManualConfigFile: editData.useManualConfigFile === true,
    }


    const setBlockLogicOptions = (data: SolverBlockLogicOptions) => {
        setEditData({ ...editData, ...{ blockLogicOptions: { ...(editData.blockLogicOptions || {}), ...data } } })
    }
    const setConstructionHeuristicOptions = (data: SolverConstructionHeuristicOptions) => {
        setEditData({ ...editData, ...{ constructionHeuristicOptions: { ...(editData.constructionHeuristicOptions || {}), ...data } } })
    }
    const seMandatoryEntityOptions = (data: SolverMandatoryEntityOptions) => {
        setEditData({ ...editData, ...{ mandatoryEntityOptions: { ...(editData.mandatoryEntityOptions || {}), ...data } } })
    }
    const setBaseEditData = (data: SolverConfigs) => {
        setEditData({ ...editData, ...data })
    }


    return {
        setEditData,
        editData,
        viewData,
        setBlockLogicOptions,
        setConstructionHeuristicOptions,
        seMandatoryEntityOptions,
        setBaseEditData
    }
}


/**
 * Main component
 * @param param0
 * @returns
 */
const SolverSettingsOptions: React.FC<SolverSettingsOptionsProps> = (props) => {

    //const [editData , setEditData] = useState({...{},...solverConfigs});
    const ctrl = props.controller;
    const setEditData = ctrl.setEditData;
    const editData = ctrl.editData;
    const viewData = ctrl.viewData;
    const setBlockLogicOptions = ctrl.setBlockLogicOptions;
    const setConstructionHeuristicOptions = ctrl.setConstructionHeuristicOptions;
    const seMandatoryEntityOptions = ctrl.seMandatoryEntityOptions;
    const setBaseEditData = ctrl.setBaseEditData;

    return (
        <div className="text-gray-700">
            <InputGroupWrapper>
                <ToggleField
                    title={'Utiliser la résolution par "Bloc"'}
                    checked={viewData.useBlockLogic === true}
                    onChange={(e) => setBlockLogicOptions({ apply: e.target.checked })}
                />
                {
                    viewData.useBlockLogic === true ? (<>
                        <QtyInputField
                            title={labels.INPUT_MAX_TIME}
                            value={viewData.blockLogicMaxTime}
                            onChange={(e) => setBlockLogicOptions({ maxTimeInMilli: safeParseInputTimeSecondsToMilli(e.target.value) })}
                        />
                        <QtyInputField
                            title={labels.INPUT_ACC_COUNT_LIMIT}
                            value={viewData.blockLogicAcceptedCountLimit}
                            onChange={(e) => setBlockLogicOptions({ acceptedCountLimit: safeParseInputIntNumber(e.target.value) })}
                        />
                        <QtyInputField
                            title={labels.INPUT_UNAPROVED_STEPS}
                            value={viewData.blockLogicUnimprovedStepCountLimit}
                            onChange={(e) => setBlockLogicOptions({ unimprovedStepCountLimit: safeParseInputIntNumber(e.target.value) })}
                        />
                        <QtyInputField
                            title={labels.INPUT_TABU_SIZE}
                            value={viewData.blockLogicTabuSize}
                            onChange={(e) => setBlockLogicOptions({ tabuSize: safeParseInputIntNumber(e.target.value) })}
                        />
                    </>) : ''
                }
            </InputGroupWrapper>

            <InputGroupWrapper>
                <ToggleField
                    title={'Utiliser la priorisation des quarts obligatoires'}
                    checked={viewData.useMandatoryEntity !== false}
                    onChange={(e) => seMandatoryEntityOptions({ apply: e.target.checked })}
                />
                {
                    viewData.useMandatoryEntity === true ? (<>
                        <QtyInputField
                            title={labels.INPUT_MAX_TIME}
                            value={viewData.mandatoryEntityMaxTime}
                            onChange={(e) => seMandatoryEntityOptions({ maxTimeInMilli: safeParseInputTimeSecondsToMilli(e.target.value) })}
                        />
                        <QtyInputField
                            title={labels.INPUT_ACC_COUNT_LIMIT}
                            value={viewData.mandatoryEntityAcceptedCountLimit}
                            onChange={(e) => seMandatoryEntityOptions({ acceptedCountLimit: safeParseInputIntNumber(e.target.value) })}
                        />
                        <QtyInputField
                            title={labels.INPUT_UNAPROVED_STEPS}
                            value={viewData.mandatoryEntityUnimprovedStepCountLimit}
                            onChange={(e) => seMandatoryEntityOptions({ unimprovedStepCountLimit: safeParseInputIntNumber(e.target.value) })}
                        />
                        <QtyInputField
                            title={labels.INPUT_TABU_SIZE}
                            value={viewData.mandatoryEntityTabuSize}
                            onChange={(e) => seMandatoryEntityOptions({ tabuSize: safeParseInputIntNumber(e.target.value) })}
                        />
                    </>) : ''
                }
            </InputGroupWrapper>

            <InputGroupWrapper>
                <ToggleField
                    title={'Utiliser la méthode de résolution simple'}
                    checked={viewData.useConstructionHeuristic !== false}
                    onChange={(e) => setConstructionHeuristicOptions({ apply: e.target.checked })}
                />
                {
                    viewData.useConstructionHeuristic === true ? (
                        <>
                            <QtyInputField
                                title={labels.INPUT_MAX_TIME}
                                value={viewData.constructionHeuristicMaxTime}
                                onChange={(e) => setConstructionHeuristicOptions({ maxTimeInMilli: safeParseInputTimeSecondsToMilli(e.target.value) })}
                            />
                            <QtyInputField
                                title={labels.INPUT_ACC_COUNT_LIMIT}
                                value={viewData.constructionHeuristicAcceptedCountLimit}
                                onChange={(e) => setConstructionHeuristicOptions({ acceptedCountLimit: safeParseInputIntNumber(e.target.value) })}
                            />
                            <QtyInputField
                                title={labels.INPUT_UNAPROVED_STEPS}
                                value={viewData.constructionHeuristicUnimprovedStepCountLimit}
                                onChange={(e) => setConstructionHeuristicOptions({ unimprovedStepCountLimit: safeParseInputIntNumber(e.target.value) })}
                            />
                            <QtyInputField
                                title={labels.INPUT_TABU_SIZE}
                                value={viewData.constructionHeuristicTabuSize}
                                onChange={(e) => setConstructionHeuristicOptions({ tabuSize: safeParseInputIntNumber(e.target.value) })}
                            />
                        </>
                    ) : ''
                }
            </InputGroupWrapper>

            <InputGroupWrapper>
                <ToggleField
                    title={'Utiliser de la configuration XML'}
                    checked={viewData.useManualConfigFile === true}
                    onChange={(e) => setBaseEditData({ useManualConfigFile: e.target.checked })}
                />
            </InputGroupWrapper>

        </div>
    );
};

export default SolverSettingsOptions;
